import type { ConsiderationListQuery } from '@/api/considerations';
import {
  CONSIDERATION_STATUSES,
  isConsiderationStatus,
  type ConsiderationStatus,
} from '@/api/vocabularies';

/**
 * **The URL is the candidate filter's source of truth, and this module is the whole contract.**
 *
 * `DESIGN.md` §6.3 puts search, a vacancy filter and a status filter above the table; §6.2's Section 3
 * links into it pre-filtered by status; a filtered list has to survive a reload and the back button;
 * and the "visible chip" needs one place to read what is applied from. All four are the same
 * requirement — the filter lives in the address bar — so the parameter names, the defaulting, the
 * validation and the mapping onto the API's own query are decided **here** and nowhere else.
 *
 * ```
 * /candidates?status=NEW&status=OFFER&vacancy=3&search=ivan&page=2
 * ```
 *
 * | In the address bar | On the wire | Why |
 * |---|---|---|
 * | `status` (repeatable) | `status` (repeated, never `status[]`) | §6.3's status filter; see `serializeQuery` |
 * | `vacancy` | `role_id` | the app says *vacancy*, the API says *role* — see below |
 * | `search` | `search` | matches the person's name |
 * | `page` | `page` (+ `page_size`) | §6.3's list is paginated at 50 |
 *
 * ⚠️ **`vacancy=` in the address bar is `role_id=` on the wire, and the rename stops at the client.**
 * `backend/CONTEXT.md` pins `Role` as an internal term — *"always means a vacancy and never access
 * rights"* — in a product that also has access `Group`s, so every user-visible string and every
 * address says *vacancy* while the API stays `/api/v1/roles/` and `role_id`. That is the shipped
 * `/vacancies` ↔ `/api/v1/roles/` precedent recorded in `src/app/navItems.ts`, applied to a query
 * parameter. The translation happens once, in `toConsiderationQuery`.
 *
 * ⚠️ **A link into this screen is built with `candidatesUrl()`, never by writing the query string
 * out.** TASK-068 turns §6.2's pipeline segments into links and TASK-063 writes every control's
 * change back into the URL; a second place that spells `?status=` is a second place that can spell
 * it wrong, and a filter the API ignores answers with *every* row while the chip claims a filter is
 * applied (§6.3: *"a filtered list that looks unfiltered is how people conclude a candidate was
 * lost"*).
 */

/** The screen's own address, so a caller building a link does not restate it either. */
export const CANDIDATES_PATH = '/candidates';

/**
 * The four parameter names, as data rather than as literals scattered through components. Reading
 * and writing both go through this object, so a rename is one edit and cannot half-happen.
 */
export const CANDIDATE_URL_PARAMS = {
  status: 'status',
  vacancy: 'vacancy',
  search: 'search',
  page: 'page',
} as const;

/**
 * §6.3: *"Default filter: active statuses only (`NEW`, `INTERVIEWING`, `OFFER`) — with a visible chip
 * showing a filter is applied."*
 *
 * ⚠️ **It is the screen's default, not the API's.** `GET /api/v1/considerations/` applies no filter by
 * design and says so in its own docstring, precisely so that a caller cannot mistake an empty tenant
 * for a filtered one. The consequence is the chip: the client hides `HIRED` and `REJECTED` rows by
 * default, so it has to say out loud that it is doing so.
 */
export const ACTIVE_STATUSES: ConsiderationStatus[] = ['NEW', 'INTERVIEWING', 'OFFER'];

/**
 * The page size this screen paginates by, **stated rather than inherited**.
 *
 * `DEFAULT_PAGE_SIZE` on the backend is 50 (`apps/people/api.py`) and `?page_size=` is capped at 100
 * (`NINJA_MAX_PER_PAGE_SIZE`). The range sentence — *"Showing 1–50 of 137"* — is arithmetic over the
 * page size, so the client sends the size it does that arithmetic with instead of assuming the
 * server's default matches: a default changed on the backend would otherwise silently misnumber the
 * range rather than break anything visible.
 */
export const CANDIDATE_PAGE_SIZE = 50;

/** What the address bar says, after validation and after §6.3's default has been applied. */
export interface CandidateFilters {
  /**
   * Exactly the statuses the URL named, unrecognised values dropped. **Empty means the URL named
   * none**, which is what the default below keys on — not "no candidates match anything".
   */
  status: ConsiderationStatus[];
  /** What is actually sent: the URL's statuses, or §6.3's active-only default when it named none. */
  appliedStatus: ConsiderationStatus[];
  /** Whether `appliedStatus` is the default rather than a choice — the chip renders on this. */
  statusIsDefault: boolean;
  /** The vacancy narrowed to, in the app's vocabulary. `role_id` on the wire. */
  vacancy: number | null;
  search: string;
  /** One-based, as the API counts pages. */
  page: number;
}

/** What a caller building a link may say. Anything omitted is left out of the address entirely. */
export interface CandidateFilterInput {
  status?: readonly ConsiderationStatus[];
  vacancy?: number | null;
  search?: string;
  page?: number;
}

/**
 * Whether a hand-typed status is one the API will accept — `isConsiderationStatus`, the guard derived
 * from the OpenAPI document (`@/api/vocabularies`).
 *
 * It was defined here until TASK-068, which needed the same question asked of a **payload** rather
 * than of an address (§6.2 Section 3's `PipelineSegment.status` is `string` on the wire), so it moved
 * beside `isRecommendation` and this module reads it from there. Re-exported because it is part of
 * this contract as much as of that one: a status added on the backend is admitted without an edit, and
 * a typo in an address is **dropped rather than sent** — `?status=NEWW` is a 422 from django-ninja,
 * and a mistyped URL must not become §8's error state when the honest answer is "that filter does not
 * exist".
 */
export { isConsiderationStatus };

/** A positive integer, or nothing. `?page=0`, `?page=-2` and `?page=two` are all "nothing". */
function positiveInteger(raw: string | null): number | null {
  if (raw === null || raw.trim() === '') return null;
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 ? value : null;
}

/**
 * Reads the filter out of the address bar. **Never throws and never produces a value the API would
 * refuse** — an unknown `status`, a non-numeric `vacancy` and a `page` of `0` are dropped, because
 * the alternative is that a URL somebody typed or shortened by hand renders an error state.
 */
export function readCandidateFilters(params: URLSearchParams): CandidateFilters {
  const named = params.getAll(CANDIDATE_URL_PARAMS.status).filter(isConsiderationStatus);
  // Deduplicated: `?status=NEW&status=NEW` is one filter, and it must not become two cache keys.
  const status = [...new Set(named)];

  return {
    status,
    appliedStatus: status.length > 0 ? status : [...ACTIVE_STATUSES],
    statusIsDefault: status.length === 0,
    vacancy: positiveInteger(params.get(CANDIDATE_URL_PARAMS.vacancy)),
    search: (params.get(CANDIDATE_URL_PARAMS.search) ?? '').trim(),
    page: positiveInteger(params.get(CANDIDATE_URL_PARAMS.page)) ?? 1,
  };
}

/**
 * Whether the viewer narrowed anything **themselves** — the condition for offering *Clear filters*
 * (TASK-063). §6.3's default is a filter, but it is not one the viewer chose, so clearing is offered
 * only once there is something of theirs to clear.
 */
export function hasChosenFilters(filters: CandidateFilters): boolean {
  return !filters.statusIsDefault || filters.vacancy !== null || filters.search !== '';
}

/**
 * Whether anything on screen **could be hiding rows** — which of §6.3's two empty states an empty
 * page is (TASK-063).
 *
 * ⚠️ **Deliberately not the same question as `hasChosenFilters`, and the difference is the honest
 * part.** Two cases separate them:
 *
 *  * *the default, untouched* — three of five statuses, so rows **can** be hidden, and yet this
 *    returns `false`. The client cannot tell an empty tenant from one whose every candidate is
 *    `HIRED` without asking a second time, and doubling the requests on this screen (300/min per
 *    user, BUG-011) to relabel a sentence is a bad trade. So the sentence is *"No candidates yet."*
 *    and the mitigation is that the chip and *Show all statuses* stay on screen in the empty state —
 *    the viewer can see the filter and undo it in one click.
 *  * *all five statuses named, nothing else* — the viewer chose, but the choice excludes nothing, so
 *    an empty page means an empty tenant and *"No candidates match these filters."* would be a
 *    riddle.
 *
 * ⚠️ **The page counts, and it was left out — a sprint-end review finding (`code-reviewer`,
 * 2026-07-28).** `/candidates?page=99` on a tenant holding 137 candidates has a default status, no
 * vacancy and no search, so this returned `false` and the screen rendered *"No candidates yet."* with
 * **Add candidate** directly above *"Showing 0–0 of 137"* — §6.3's own named failure inverted, and
 * the one action ADR 0002 exists to prevent offered to somebody whose address merely overshot. A page
 * past the end is the plainest case there is of *something on screen is hiding rows*, and its remedy
 * is the filtered state's: *Clear filters*, which drops `page` with everything else.
 *
 * ⚠️ **Not clamped to the last page, deliberately.** `count` is only known once the answer arrives,
 * so clamping would mean the screen rewriting the address from a response — a second writer of the
 * page, a history entry nobody navigated to, and a `readCandidateFilters` that could no longer be a
 * pure function of `URLSearchParams` (which is what lets `candidateTable.test.tsx` check the contract
 * without a server). The URL stays exactly what was typed; the screen says the page is empty, offers
 * the way back, and `Next` is disabled on an empty page so it cannot walk on to page 100.
 */
export function couldBeHidingRows(filters: CandidateFilters): boolean {
  if (filters.vacancy !== null || filters.search !== '' || filters.page > 1) return true;
  if (filters.statusIsDefault) return false;

  return filters.appliedStatus.length < CONSIDERATION_STATUSES.length;
}

/**
 * The query string for a filter set — what `setSearchParams` is given and what `candidatesUrl`
 * builds on.
 *
 * A parameter carrying nothing is **absent**, not empty: `?search=` is a value to Pydantic and
 * `?page=1` is noise in an address people copy to each other. An omitted `status` is what §6.3's
 * default keys on, so the default is never written into the URL — clearing it means naming every
 * status explicitly (`CONSIDERATION_STATUSES`), which is the one thing that says *"show me `HIRED`
 * and `REJECTED` too"* in a form that survives a reload.
 */
export function candidateSearch(filters: CandidateFilterInput = {}): string {
  const params = new URLSearchParams();

  for (const status of filters.status ?? []) params.append(CANDIDATE_URL_PARAMS.status, status);
  if (filters.vacancy != null) params.set(CANDIDATE_URL_PARAMS.vacancy, String(filters.vacancy));
  if (filters.search) params.set(CANDIDATE_URL_PARAMS.search, filters.search);
  if (filters.page != null && filters.page > 1) {
    params.set(CANDIDATE_URL_PARAMS.page, String(filters.page));
  }

  return params.toString();
}

/**
 * The address of the candidate list under a filter — **the way another screen links into it**.
 *
 * §6.2's Section 3 needs `candidatesUrl({ status: ['NEW'] })` and must not have to know that the
 * parameter is repeatable, that `page` is dropped at 1, or that `vacancy` becomes `role_id` later
 * (TASK-068).
 */
export function candidatesUrl(filters: CandidateFilterInput = {}): string {
  const search = candidateSearch(filters);
  return search ? `${CANDIDATES_PATH}?${search}` : CANDIDATES_PATH;
}

/**
 * The address bar translated into `GET /api/v1/considerations/`'s own query — the **only** place the
 * two vocabularies meet.
 *
 * `status` goes out as repeated parameters because `http.ts`'s `paramsSerializer` produces them;
 * axios's default (`status[]=NEW`) is ignored by django-ninja, which would answer with every row
 * while the chip claims a filter is applied. `search: ''` is sent as nothing at all rather than as an
 * empty parameter.
 */
export function toConsiderationQuery(filters: CandidateFilters): ConsiderationListQuery {
  return {
    status: filters.appliedStatus,
    role_id: filters.vacancy,
    search: filters.search || undefined,
    page: filters.page,
    page_size: CANDIDATE_PAGE_SIZE,
  };
}
